// NymblStudio DOM-inspector loader (Nymbl App Spec §2.9, SHOULD).
//
// When this app runs in dev INSIDE the studio's Build → App iframe, the studio
// can't reach our DOM (cross-origin). This loader waits for the studio's init
// handshake and then injects the studio-served inspector bridge script, which
// does the actual element highlighting/picking and reports back via
// postMessage. It also reports runtime errors to the studio, so the preview can
// show them. Dev-only — compiled out of production builds — and inert when
// the page isn't iframed.

interface StudioAppError {
  message: string;
  stack: string;
  url: string;
}

interface ErrorGateOptions {
  repeatWindowMs: number;
  rateWindowMs: number;
  maxPerWindow: number;
}

const PENDING_ERROR_LIMIT = 20;

function isInitMessage(data: unknown): boolean {
  return (
    typeof data === "object" &&
    data !== null &&
    "type" in data &&
    data.type === "nymbl-inspector:init"
  );
}

// Security boundary: only the studio may trigger script injection. Local dev
// pairs localhost:8080 (studio) with localhost:<port> (app); on workstations
// the app at <project>.<workstation>.nymbl.studio accepts only its parent
// domain <workstation>.nymbl.studio. Behind Fleet the studio and the app are
// siblings (ws.fleet.nymbl.studio and <project>.preview.fleet.nymbl.studio),
// so the app also accepts the Fleet studio beside its preview domain.
// Everything else is silently ignored.
const FLEET_STUDIO_PREFIX = "ws.";

export function isTrustedStudioHost(studioHost: string, appHost: string): boolean {
  if (studioHost === "localhost" || studioHost === "127.0.0.1") return true;
  if (appHost.endsWith(`.${studioHost}`)) return true;
  if (!studioHost.startsWith(FLEET_STUDIO_PREFIX)) return false;
  const fleetDomain = studioHost.slice(FLEET_STUDIO_PREFIX.length);
  // At least three labels (fleet.nymbl.studio). Otherwise a workstation named
  // "ws" (ws.nymbl.studio) would be trusted by every app of a workstation named
  // "preview" (<project>.preview.nymbl.studio).
  if (fleetDomain.split(".").length < 3) return false;
  return appHost.endsWith(`.preview.${fleetDomain}`);
}

function isTrustedStudioOrigin(origin: string): boolean {
  try {
    return isTrustedStudioHost(new URL(origin).hostname, window.location.hostname);
  } catch {
    return false;
  }
}

export function toStudioAppError(reason: unknown, url: string): StudioAppError {
  if (reason instanceof Error) {
    return { message: reason.message || reason.name, stack: reason.stack ?? "", url };
  }
  return { message: String(reason), stack: "", url };
}

function firstStackFrame(stack: string): string {
  return stack.split("\n").find((line) => line.trim().startsWith("at ")) ?? "";
}

export function createErrorGate(options: ErrorGateOptions) {
  const lastSeenByError = new Map<string, number>();
  let recentSends: number[] = [];
  return (error: StudioAppError, now: number): boolean => {
    const key = `${error.message}\n${firstStackFrame(error.stack)}`;
    const lastSeen = lastSeenByError.get(key);
    if (lastSeen !== undefined && now - lastSeen < options.repeatWindowMs) return false;
    recentSends = recentSends.filter((sentAt) => now - sentAt < options.rateWindowMs);
    if (recentSends.length >= options.maxPerWindow) return false;
    lastSeenByError.set(key, now);
    recentSends.push(now);
    return true;
  };
}

function isIframed(): boolean {
  return typeof window !== "undefined" && window.self !== window.top;
}

let deliverStudioError: (reason: unknown) => void = () => {};

export function reportStudioError(reason: unknown): void {
  deliverStudioError(reason);
}

function startStudioConnection() {
  const passesErrorGate = createErrorGate({
    repeatWindowMs: 10_000,
    rateWindowMs: 60_000,
    maxPerWindow: 10,
  });
  const pendingErrors: StudioAppError[] = [];
  let studioOrigin: string | null = null;
  let injected = false;

  const postToStudio = (origin: string, message: Record<string, unknown>) => {
    window.parent.postMessage(message, origin);
  };
  const postError = (origin: string, error: StudioAppError) => {
    postToStudio(origin, { type: "nymbl-app:error", ...error });
  };
  const connectStudio = (origin: string) => {
    studioOrigin = origin;
    postToStudio(origin, { type: "nymbl-app:ready", features: ["errors"] });
    for (const error of pendingErrors.splice(0)) postError(origin, error);
  };
  const injectBridgeOnce = (origin: string) => {
    if (injected) return;
    injected = true;
    const script = document.createElement("script");
    script.src = `${origin}/inspector-bridge.js`;
    document.head.appendChild(script);
  };

  deliverStudioError = (reason) => {
    const error = toStudioAppError(reason, window.location.href);
    if (!passesErrorGate(error, Date.now())) return;
    if (studioOrigin) {
      postError(studioOrigin, error);
      return;
    }
    pendingErrors.push(error);
    if (pendingErrors.length > PENDING_ERROR_LIMIT) pendingErrors.shift();
  };

  window.addEventListener("error", (event: ErrorEvent) => {
    reportStudioError(event.error ?? event.message);
  });
  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    reportStudioError(event.reason);
  });
  window.addEventListener("message", (event: MessageEvent) => {
    if (!isInitMessage(event.data)) return;
    if (!isTrustedStudioOrigin(event.origin)) return;
    connectStudio(event.origin);
    injectBridgeOnce(event.origin);
  });
}

if (process.env.NODE_ENV !== "production" && isIframed()) {
  startStudioConnection();
}
